import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import axios from 'axios';
import {
  Activity,
  AlertTriangle,
  ArrowRight,
  Briefcase,
  ClipboardCheck,
  FileWarning,
  Gauge,
  GitBranch,
  Landmark,
  ListChecks,
  ShieldAlert,
  TrendingUp,
} from 'lucide-react';
import {
  ActionBar,
  CreditWorkspaceHero,
  DataTable,
  EmptyStateBox,
  Eyebrow,
  HeroSubtitle,
  HeroTitle,
  HeroTop,
  MetricCard,
  MetricLabel,
  MetricStrip,
  MetricSubtext,
  MetricValue,
  PanelBody,
  PanelHeader,
  PanelSubtitle,
  PanelTitle,
  RowSubtext,
  RowTitle,
  StatusPill,
  TableShell,
  WorkspaceButton,
  WorkspacePanel,
  moneyCompact,
  numberCompact,
  type Tone,
} from './shared/CreditWorkspace';
import { axiomTheme, toneAlpha, toneColor } from '../../styles/designTokens';
import { type DrawerTabKey } from './Loan360Drawer';
import { loan360Path } from '../../utils/loanNavigation';
import CreditFileImportPanel from './CreditFileImportPanel';

type CreditTab =
  | 'loans'
  | 'covenants'
  | 'distressed'
  | 'secondary'
  | 'exposure'
  | 'prepayment';

interface Props {
  onNavigate: (tab: CreditTab, context?: Record<string, string | number | undefined>) => void;
}

interface LoanRow {
  id: number | string;
  source: 'LEVERAGED_LOAN' | 'PRIVATE_CREDIT';
  securityId?: number;
  security?: { id?: number; cusip?: string; isin?: string };
  loanId?: string;
  cusip?: string;
  loanName?: string;
  borrowerName?: string;
  sponsorName?: string;
  facilityType?: string;
  seniority?: string;
  status?: string;
  currentCommitment?: number;
  originalCommitment?: number;
  fundedAmount?: number;
  unfundedAmount?: number;
  allInRate?: number;
  spreadBps?: number;
  baseRate?: string;
  floorRate?: number;
  oidPrice?: number;
  upfrontFeeBps?: number;
  maturityDate?: string;
  closingDate?: string;
  sector?: string;
  industry?: string;
  spRating?: string;
  moodysRating?: string;
  fitchRating?: string;
  pikToggle?: string;
  pikElectionCurrent?: string;
  pikSpreadBps?: number;
  ddtlCommitment?: number;
  ddtlFunded?: number;
  ddtlUndrawn?: number;
  ddtlExpiry?: string;
  currentCapacity?: number;
  globalOutstanding?: number;
  globalAvailability?: number;
  tickingFeeBps?: number;
  commitmentFeeBps?: number;
  csaBps?: number;
  stepDownSpreadBps?: number;
  stepDownTriggerLeverage?: number;
}

interface CovenantAlert {
  alertType?: string;
  message?: string;
  covenant?: {
    id?: number;
    covenantName?: string;
    borrowerName?: string;
    metricType?: string;
    thresholdValue?: number;
  };
}

interface CovenantRow {
  id?: number;
  covenantName?: string;
  borrowerName?: string;
  scope?: string;
  covenantType?: string;
  covenantCategory?: string;
  metricType?: string;
  thresholdType?: string;
  thresholdValue?: number;
  thresholdValueMax?: number;
  warningThresholdPct?: number;
  measurementFrequency?: string;
  hasStepSchedule?: boolean;
  hasEquityCure?: boolean;
  maxEquityCures?: number;
  effectiveDate?: string;
  terminationDate?: string;
  amendedBy?: string;
  amendedAt?: string;
}

interface WatchlistEntry {
  loanId?: number;
  borrowerName?: string;
  loanIdentifier?: string;
  sector?: string;
  currentExposure?: number;
  riskScore?: number;
  severity?: string;
  recommendedSituationType?: string;
  signals?: Array<{ signalType?: string; detail?: string; severity?: string }>;
}

interface DistressedSituation {
  id?: number;
  companyName?: string;
  situationType?: string;
  status?: string;
  totalExposure?: number;
  industry?: string;
  nextMilestoneDate?: string;
  probabilityOfDefault?: number;
}

interface TradeRow {
  id?: number;
  ticketType?: string;
  eventType?: string;
  status?: string;
  securityId?: number;
  securityName?: string;
  portfolioName?: string;
  quantity?: number;
  amount?: number;
  tradeDate?: string;
}

interface CreditDashboardStats {
  leveragedLoans?: {
    totalLoans?: number;
    activeLoans?: number;
    performingLoans?: number;
    totalParValue?: number;
    totalUnfunded?: number;
    totalUnfundedCommitments?: number;
  };
  covenants?: {
    activeBreaches?: number;
    breachCount?: number;
    warnings?: number;
    warningCount?: number;
    activeCovenants?: number;
    totalCovenants?: number;
  };
  distressed?: {
    activeSituations?: number;
    totalExposure?: number;
    situationsWithUpcomingDeadlines?: number;
  };
  secondaryMarket?: {
    activeListCount?: number;
    bwicsLast7Days?: number;
    totalActiveFaceValue?: number;
  };
}

interface LoadState {
  dashboard: CreditDashboardStats | null;
  loans: LoanRow[];
  covenants: CovenantRow[];
  covenantAlerts: CovenantAlert[];
  watchlist: WatchlistEntry[];
  distressed: DistressedSituation[];
  trades: TradeRow[];
  errors: string[];
}

const Shell = styled.div`
  padding: ${axiomTheme.spacing.page};
  color: ${axiomTheme.color.text.secondary};
`;

const HeaderActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  justify-content: flex-end;
`;

const LayoutGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1.35fr) minmax(360px, 0.65fr);
  gap: 1rem;

  @media (max-width: 1180px) {
    grid-template-columns: 1fr;
  }
`;

const Stack = styled.div`
  display: grid;
  gap: 1rem;
`;

const TwoColumn = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1rem;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const DecisionList = styled.div`
  display: grid;
  gap: 0.65rem;
`;

const DecisionItem = styled.div<{ $tone?: Tone }>`
  border: 1px solid ${p => toneAlpha(p.$tone || 'slate', '3d')};
  border-left: 3px solid ${p => toneColor(p.$tone || 'slate')};
  border-radius: 8px;
  background: rgba(15, 23, 42, 0.58);
  padding: 0.78rem 0.85rem;
`;

const DecisionTop = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 0.75rem;
`;

const DecisionTitle = styled.div`
  color: ${axiomTheme.color.text.primary};
  font-weight: 850;
  font-size: 0.84rem;
`;

const DecisionMeta = styled.div`
  margin-top: 0.24rem;
  color: ${axiomTheme.color.text.muted};
  font-size: 0.72rem;
  line-height: 1.4;
`;

const IconBadge = styled.div<{ $tone?: Tone }>`
  width: 38px;
  height: 38px;
  border-radius: 8px;
  display: grid;
  place-items: center;
  border: 1px solid ${p => toneAlpha(p.$tone || 'cyan', '44')};
  background: ${p => toneAlpha(p.$tone || 'cyan', '18')};
  color: ${p => toneColor(p.$tone || 'cyan')};

  svg {
    width: 19px;
    height: 19px;
  }
`;

const SectionKicker = styled.div`
  color: ${axiomTheme.color.text.faint};
  font-size: ${axiomTheme.typography.label.size};
  font-weight: ${axiomTheme.typography.label.weight};
  text-transform: uppercase;
  letter-spacing: ${axiomTheme.typography.label.tracking};
`;

const SourceGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.7rem;

  @media (max-width: 680px) {
    grid-template-columns: 1fr;
  }
`;

const SourceCard = styled.div`
  border: 1px solid ${axiomTheme.color.border.default};
  border-radius: 8px;
  background: ${axiomTheme.surface.card};
  padding: 0.82rem;
`;

const SourceValue = styled.div`
  color: ${axiomTheme.color.text.primary};
  font-size: 1.25rem;
  font-weight: 900;
`;

const SourceLabel = styled.div`
  color: ${axiomTheme.color.text.muted};
  margin-top: 0.24rem;
  font-size: 0.72rem;
  font-weight: 750;
`;

const ErrorStrip = styled.div`
  border: 1px solid rgba(248, 113, 113, 0.32);
  border-radius: 8px;
  color: #fecaca;
  background: rgba(127, 29, 29, 0.22);
  padding: 0.75rem 0.9rem;
  font-size: 0.78rem;
  margin-bottom: 1rem;
`;

const LoadingState = styled.div`
  min-height: 40px;
  display: grid;
  place-items: center;
  color: ${axiomTheme.color.text.muted};
`;

const LoanSearch = styled.input`
  width: min(320px, 100%);
  min-height: 36px;
  border: 1px solid ${axiomTheme.color.border.default};
  border-radius: 8px;
  background: rgba(2, 6, 23, 0.72);
  color: ${axiomTheme.color.text.primary};
  padding: 0 0.75rem;
  font-size: 0.78rem;

  &:focus {
    outline: none;
    border-color: ${toneColor('cyan')};
  }
`;

const deskSources = [
  ['dashboard', 'Credit dashboard', '/api/credit/dashboard'],
  ['leveragedLoans', 'Syndicated loans', '/api/credit/loans'],
  ['privateCreditLoans', 'Private credit loans', '/api/private-credit/loans'],
  ['covenants', 'Covenant package', '/api/credit/covenants?scope=ALL'],
  ['covenantAlerts', 'Covenant alerts', '/api/credit/covenants/alerts'],
  ['watchlist', 'Credit watchlist', '/api/credit/watchlist?limit=200'],
  ['distressed', 'Distressed situations', '/api/credit/distressed'],
  ['trades', 'Trade lifecycle', '/api/trade-transactions?size=500&sort=tradeDate,desc'],
] as const;
type DeskSource = typeof deskSources[number][0];

export const asArray = <T,>(value: any): T[] => {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.content)) return value.content;
  return [];
};

const num = (value: any) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

const pct = (value: any) => {
  if (value == null || Number.isNaN(Number(value))) return '--';
  return `${Number(value).toFixed(2)}%`;
};

const statusTone = (status?: string): Tone => {
  const s = (status || '').toUpperCase();
  if (s.includes('BREACH') || s.includes('DEFAULT') || s.includes('CRITICAL') || s.includes('FAILED')) return 'red';
  if (s.includes('WATCH') || s.includes('WARN') || s.includes('PENDING') || s.includes('SENT')) return 'amber';
  if (s.includes('POSTED') || s.includes('ACTIVE') || s.includes('PERFORMING') || s.includes('EXECUTED')) return 'emerald';
  if (s.includes('DRAFT') || s.includes('APPROVED')) return 'cyan';
  return 'slate';
};

// A distressed / non-accrual / defaulted loan is the single most important name
// on a credit desk, yet a paid-down, marked-down workout also carries the
// *lowest* funded balance — so a naive funded-DESC + top-N truncation would push
// it off the visible "Credit Book" and silently hide it. Loans flagged here are
// always surfaced regardless of funded rank.
const isDistressedLoan = (loan: LoanRow): boolean => {
  const s = (loan.status || '').toUpperCase();
  return (
    (loan as any).nonAccrual === true ||
    (loan as any).onNonAccrual === true ||
    s.includes('DEFAULT') ||
    s.includes('NON_ACCRUAL') ||
    s.includes('NONACCRUAL') ||
    s.includes('NON-ACCRUAL') ||
    s.includes('NON_PERFORMING') ||
    s.includes('NONPERFORMING') ||
    s.includes('WORKOUT') ||
    s.includes('RESTRUCTUR') ||
    s.includes('IMPAIRED') ||
    s.includes('DISTRESSED')
  );
};

export const normalizeLoans = (leveragedLoans: any[], privateCreditLoans: any[]): LoanRow[] => {
  const leveraged = leveragedLoans.map((loan: any) => ({
    ...loan,
    source: 'LEVERAGED_LOAN' as const,
  }));

  const privates = privateCreditLoans.map((loan: any) => ({
    ...loan,
    source: 'PRIVATE_CREDIT' as const,
  }));

  return [...privates, ...leveraged].sort((a, b) =>
    num(b.fundedAmount ?? b.currentCommitment ?? b.originalCommitment)
      - num(a.fundedAmount ?? a.currentCommitment ?? a.originalCommitment)
  );
};

const ticketInScope = (ticketType?: string) => {
  const t = (ticketType || '').toUpperCase();
  return t === 'PRIVATE_CREDIT' || t === 'LEVERAGED_LOAN';
};

const openTrade = (trade: TradeRow) => {
  const s = (trade.status || '').toUpperCase();
  return ticketInScope(trade.ticketType) && !['POSTED', 'CANCELLED', 'REJECTED'].includes(s);
};

const CreditOperatingSystemDashboard: React.FC<Props> = ({ onNavigate }) => {
  const [state, setState] = useState<LoadState>({
    dashboard: null,
    loans: [],
    covenants: [],
    covenantAlerts: [],
    watchlist: [],
    distressed: [],
    trades: [],
    errors: [],
  });
  const [pending, setPending] = useState<DeskSource[]>(deskSources.map(([key]) => key));
  const [loaded, setLoaded] = useState<DeskSource[]>([]);
  const request = useRef<AbortController | null>(null);
  const [searchParams] = useSearchParams();
  const [loanSearch, setLoanSearch] = useState('');
  const [showAllLoans, setShowAllLoans] = useState(true);

  const loadCreditDesk = useCallback(async () => {
    request.current?.abort();
    const controller = new AbortController();
    request.current = controller;
    setPending(deskSources.map(([key]) => key));
    // Retain successful records while refreshing. Each response updates only its
    // own source; a slow surveillance request never blocks the loan workspace.
    await Promise.all(deskSources.map(async ([key, label, url]) => {
      try {
        const response = await axios.get(url, { signal: controller.signal, timeout: 30000 });
        if (controller.signal.aborted) return;
        setState(previous => {
          const next = { ...previous, errors: previous.errors.filter(error => error !== label) };
          if (key === 'leveragedLoans' || key === 'privateCreditLoans') {
            next.loans = normalizeLoans(
              key === 'leveragedLoans' ? asArray(response.data) : previous.loans.filter(loan => loan.source === 'LEVERAGED_LOAN'),
              key === 'privateCreditLoans' ? asArray(response.data) : previous.loans.filter(loan => loan.source === 'PRIVATE_CREDIT'),
            );
          } else if (key === 'dashboard') {
            next.dashboard = response.data;
          } else {
            next[key] = asArray(response.data);
          }
          return next;
        });
        setLoaded(previous => previous.includes(key) ? previous : [...previous, key]);
      } catch {
        if (controller.signal.aborted) return;
        setState(previous => ({ ...previous, errors: previous.errors.includes(label) ? previous.errors : [...previous.errors, label] }));
      } finally {
        if (!controller.signal.aborted) setPending(previous => previous.filter(item => item !== key));
      }
    }));
  }, []);

  useEffect(() => {
    void loadCreditDesk();
    return () => request.current?.abort();
  }, [loadCreditDesk]);

  const ready = (...keys: DeskSource[]) => keys.every(key => loaded.includes(key));
  const sectionMessage = (...keys: DeskSource[]) => keys.some(key => pending.includes(key))
    ? 'Loading records…' : 'Records unavailable. Please retry.';
  const loadingNotice = pending.length > 0 && <LoadingState role="status" aria-live="polite">
    {loaded.length ? 'Updating' : 'Loading'} {deskSources.filter(([key]) => pending.includes(key)).map(([, label]) => label.toLowerCase()).join(', ')}…
  </LoadingState>;

  // Loan 360 is its own page (/portfolio-management/credit/loans/{lane}/{id}); the desk links to it.
  const navigate = useNavigate();
  const openLoan360 = (loan: LoanRow, tab: DrawerTabKey = 'overview') => navigate(loan360Path(loan.source, loan.id, tab));

  const totals = useMemo(() => {
    const funded = state.loans.reduce((sum, loan) => sum + num(loan.fundedAmount), 0);
    const commitment = state.loans.reduce(
      (sum, loan) => sum + num(loan.currentCommitment ?? loan.originalCommitment),
      0
    );
    const unfunded = state.loans.reduce((sum, loan) => sum + num(loan.unfundedAmount), 0);
    const openTrades = state.trades.filter(openTrade);
    const alerts = state.covenantAlerts.length;
    const watchlistHigh = state.watchlist.filter(w => num(w.riskScore) >= 40).length;
    const activeDistressed = state.distressed.filter(d =>
      !['DISMISSED', 'EMERGED', 'CLOSED'].includes((d.status || '').toUpperCase())
    ).length;

    return { funded, commitment, unfunded, openTrades, alerts, watchlistHigh, activeDistressed };
  }, [state]);

  const sponsorConcentration = useMemo(() => {
    const map = new Map<string, number>();
    state.loans.forEach(loan => {
      if (!loan.sponsorName) return;
      map.set(loan.sponsorName, (map.get(loan.sponsorName) || 0) + num(loan.fundedAmount ?? loan.currentCommitment));
    });
    return Array.from(map.entries())
      .map(([sponsor, exposure]) => ({ sponsor, exposure }))
      .sort((a, b) => b.exposure - a.exposure)
      .slice(0, 6);
  }, [state.loans]);

  const openTrades = state.trades.filter(openTrade).slice(0, 8);
  // Show the top-10 loans by funded exposure, but never let a distressed /
  // non-accrual / defaulted name fall off the list just because it has been
  // paid down or marked down (lowest funded balance → bottom of the funded-DESC
  // sort). Any distressed loan outside the funded top-10 is appended so it stays
  // visible. Commitment-only facilities are also appended: a $0 funded balance
  // is not "no exposure" when the fund owns a material unfunded obligation.
  // state.loans is already sorted funded-DESC by normalizeLoans.
  const topLoans = (() => {
    const top = state.loans.slice(0, 10);
    const seen = new Set(top.map(loan => `${loan.source}-${loan.id}`));
    const requiredTail = state.loans.filter(
      loan => (isDistressedLoan(loan)
        || (num(loan.fundedAmount) === 0 && num(loan.unfundedAmount) > 0))
        && !seen.has(`${loan.source}-${loan.id}`)
    );
    return [...top, ...requiredTail];
  })();
  const normalizedLoanSearch = loanSearch.trim().toLowerCase();
  const visibleLoans = normalizedLoanSearch
    ? state.loans.filter(loan => [
        loan.borrowerName,
        loan.loanName,
        loan.loanId,
        loan.security?.cusip,
        loan.sponsorName,
      ].some(value => String(value || '').toLowerCase().includes(normalizedLoanSearch)))
    : showAllLoans ? state.loans : topLoans;

  // Links from before Loan 360 had its own page (bookmarks, saved Ask Axiom answers, the covenant screen's
  // openLoan=SOURCE:ID) still land on the loan.
  const legacyLink = searchParams.get('openLoan');
  const linkedSource = (searchParams.get('loanSource') || (legacyLink ? legacyLink.split(':')[0] : '') || '').toUpperCase();
  const linkedId = (searchParams.get('loanId') || (legacyLink ? legacyLink.split(':')[1] : '') || '').trim();
  if ((linkedSource === 'PRIVATE_CREDIT' || linkedSource === 'LEVERAGED_LOAN') && linkedId) {
    const tab = searchParams.get('loanTab') || (legacyLink ? 'covenants' : 'overview');
    return <Navigate replace to={loan360Path(linkedSource, linkedId, tab)} />;
  }

  return (
    <Shell>
      {loadingNotice}

      {state.errors.length > 0 && (
        <ErrorStrip>
          Data unavailable: {state.errors.join(', ')}. Previously loaded records remain visible and may be out of date. <WorkspaceButton disabled={pending.length > 0} onClick={() => void loadCreditDesk()}>Retry</WorkspaceButton>
        </ErrorStrip>
      )}

      <CreditWorkspaceHero>
        <HeroTop>
          <div>
            <Eyebrow $tone="cyan">Credit Operating System</Eyebrow>
            <HeroTitle>Private Credit & Syndicated Loan Desk</HeroTitle>
            <HeroSubtitle>
              Portfolio, lifecycle, covenant, surveillance, and workout controls anchored to the IBOR posting model.
            </HeroSubtitle>
          </div>
          <HeaderActions>
            <WorkspaceButton $variant="primary" onClick={() => onNavigate('loans')}>
              Loan Book <ArrowRight size={14} />
            </WorkspaceButton>
            <WorkspaceButton onClick={() => onNavigate('covenants')}>
              Covenants <ArrowRight size={14} />
            </WorkspaceButton>
            <WorkspaceButton onClick={() => onNavigate('distressed')}>
              Workout <ArrowRight size={14} />
            </WorkspaceButton>
          </HeaderActions>
        </HeroTop>

        <MetricStrip>
          <MetricCard $tone="blue">
            <MetricLabel>Funded Exposure</MetricLabel>
            <MetricValue>{ready('leveragedLoans', 'privateCreditLoans', 'dashboard') ? moneyCompact(totals.funded || state.dashboard?.leveragedLoans?.totalParValue) : '—'}</MetricValue>
            <MetricSubtext>{ready('leveragedLoans', 'privateCreditLoans') ? numberCompact(state.loans.length) : '—'} private and syndicated facilities</MetricSubtext>
          </MetricCard>
          <MetricCard $tone="cyan">
            <MetricLabel>Total Commitment</MetricLabel>
            <MetricValue>{ready('leveragedLoans', 'privateCreditLoans') ? moneyCompact(totals.commitment) : '—'}</MetricValue>
            <MetricSubtext>{ready('leveragedLoans', 'privateCreditLoans') ? moneyCompact(totals.unfunded) : '—'} unfunded</MetricSubtext>
          </MetricCard>
          <MetricCard $tone={!ready('covenantAlerts', 'dashboard') ? 'slate' : totals.alerts > 0 ? 'amber' : 'emerald'}>
            <MetricLabel>Covenant Alerts</MetricLabel>
            <MetricValue>{ready('covenantAlerts', 'dashboard') ? numberCompact(totals.alerts) : '—'}</MetricValue>
            <MetricSubtext>
              {ready('dashboard') ? numberCompact(state.dashboard?.covenants?.activeCovenants ?? state.dashboard?.covenants?.totalCovenants) : '—'} active covenants
            </MetricSubtext>
          </MetricCard>
          <MetricCard $tone={!ready('trades') ? 'slate' : totals.openTrades.length > 0 ? 'amber' : 'emerald'}>
            <MetricLabel>Lifecycle Queue</MetricLabel>
            <MetricValue>{ready('trades') ? numberCompact(totals.openTrades.length) : '—'}</MetricValue>
            <MetricSubtext>Non-posted credit tickets (500 most recent trades)</MetricSubtext>
          </MetricCard>
          <MetricCard $tone={totals.watchlistHigh > 0 ? 'red' : 'slate'}>
            <MetricLabel>Watchlist Risk</MetricLabel>
            <MetricValue>{ready('watchlist') ? numberCompact(totals.watchlistHigh) : '—'}</MetricValue>
            <MetricSubtext>{ready('distressed') ? numberCompact(totals.activeDistressed) : '—'} active workout situations</MetricSubtext>
          </MetricCard>
        </MetricStrip>
      </CreditWorkspaceHero>

      <div style={{ marginBottom: '1rem' }}>
        <CreditFileImportPanel onImported={loadCreditDesk} />
      </div>

      <LayoutGrid>
        <Stack>
          <WorkspacePanel>
            <PanelHeader>
              <div>
                <SectionKicker>Front Office</SectionKicker>
                <PanelTitle>Credit Book</PanelTitle>
                <PanelSubtitle>Searchable private credit and syndicated loan inventory from current system records.</PanelSubtitle>
              </div>
              <ActionBar>
                <LoanSearch
                  aria-label="Search credit book"
                  placeholder="Search borrower, loan ID, CUSIP..."
                  value={loanSearch}
                  onChange={event => setLoanSearch(event.target.value)}
                />
                <WorkspaceButton onClick={() => setShowAllLoans(value => !value)}>
                  {showAllLoans ? 'Largest exposures' : 'View all loans'}
                </WorkspaceButton>
                <WorkspaceButton onClick={() => onNavigate('loans')}>Open loan workspace</WorkspaceButton>
                <WorkspaceButton onClick={() => onNavigate('exposure')}>Exposure view</WorkspaceButton>
              </ActionBar>
            </PanelHeader>
            <PanelBody>
              {!ready('leveragedLoans', 'privateCreditLoans') && (visibleLoans.length === 0) ? <EmptyStateBox>{sectionMessage('leveragedLoans', 'privateCreditLoans')}</EmptyStateBox> : visibleLoans.length === 0 ? (
                <EmptyStateBox>No private credit or syndicated loan records are available for this tenant.</EmptyStateBox>
              ) : (
                <TableShell>
                  <DataTable>
                    <thead>
                      <tr>
                        <th>Borrower</th>
                        <th>Type</th>
                        <th>Facility</th>
                        <th>Status</th>
                        <th>Funded</th>
                        <th>Unfunded</th>
                        <th>All-in</th>
                        <th>Maturity</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {visibleLoans.map(loan => (
                        <tr key={`${loan.source}-${loan.id}`} onClick={() => openLoan360(loan)} style={{ cursor: 'pointer' }}>
                          <td>
                            <RowTitle>{loan.borrowerName || loan.loanName || 'Unnamed borrower'}</RowTitle>
                            <RowSubtext>{loan.sponsorName || loan.sector || loan.loanId || 'No sponsor/sector mapped'}</RowSubtext>
                          </td>
                          <td>
                            <StatusPill $tone={loan.source === 'PRIVATE_CREDIT' ? 'violet' : 'blue'}>
                              {loan.source === 'PRIVATE_CREDIT' ? 'Private' : 'Syndicated'}
                            </StatusPill>
                          </td>
                          <td>{loan.facilityType || '--'}</td>
                          <td><StatusPill $tone={statusTone(loan.status)}>{loan.status || '--'}</StatusPill></td>
                          <td>{moneyCompact(loan.fundedAmount)}</td>
                          <td>{moneyCompact(loan.unfundedAmount)}</td>
                          <td>{pct(loan.allInRate)}</td>
                          <td>{loan.maturityDate || '--'}</td>
                          <td>
                            <WorkspaceButton onClick={(event) => { event.stopPropagation(); openLoan360(loan); }}>
                              Loan 360
                            </WorkspaceButton>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </DataTable>
                </TableShell>
              )}
            </PanelBody>
          </WorkspacePanel>

          <TwoColumn>
            <WorkspacePanel>
              <PanelHeader>
                <div>
                  <SectionKicker>Middle Office</SectionKicker>
                  <PanelTitle>Trade Lifecycle Queue</PanelTitle>
                  <PanelSubtitle>Private and syndicated credit tickets not yet posted to IBOR.</PanelSubtitle>
                </div>
                <IconBadge $tone="amber"><ListChecks /></IconBadge>
              </PanelHeader>
              <PanelBody>
                {!ready('trades') && (openTrades.length === 0) ? <EmptyStateBox>{sectionMessage('trades')}</EmptyStateBox> : openTrades.length === 0 ? (
                  <EmptyStateBox>No open private or syndicated loan trade lifecycle items.</EmptyStateBox>
                ) : (
                  <DecisionList>
                    {openTrades.map(trade => (
                      <DecisionItem key={trade.id} $tone={statusTone(trade.status)}>
                        <DecisionTop>
                          <div>
                            <DecisionTitle>{trade.securityName || `${trade.ticketType || 'Credit'} ticket #${trade.id}`}</DecisionTitle>
                            <DecisionMeta>
                              {trade.eventType || 'Trade'} · {trade.portfolioName || 'Portfolio pending'} · {moneyCompact(trade.amount ?? trade.quantity)}
                            </DecisionMeta>
                          </div>
                          <StatusPill $tone={statusTone(trade.status)}>{trade.status || 'OPEN'}</StatusPill>
                        </DecisionTop>
                      </DecisionItem>
                    ))}
                  </DecisionList>
                )}
              </PanelBody>
            </WorkspacePanel>

            <WorkspacePanel>
              <PanelHeader>
                <div>
                  <SectionKicker>Surveillance</SectionKicker>
                  <PanelTitle>Covenant Exceptions</PanelTitle>
                  <PanelSubtitle>Alerts generated by the unified covenant center.</PanelSubtitle>
                </div>
                <IconBadge $tone={state.covenantAlerts.length > 0 ? 'amber' : 'emerald'}><ClipboardCheck /></IconBadge>
              </PanelHeader>
              <PanelBody>
                {!ready('covenantAlerts') && (state.covenantAlerts.length === 0) ? <EmptyStateBox>{sectionMessage('covenantAlerts')}</EmptyStateBox> : state.covenantAlerts.length === 0 ? (
                  <EmptyStateBox>No covenant alerts are open.</EmptyStateBox>
                ) : (
                  <DecisionList>
                    {state.covenantAlerts.slice(0, 8).map((alert, idx) => (
                      <DecisionItem key={`${alert.covenant?.id || idx}-${alert.alertType}`} $tone={statusTone(alert.alertType)}>
                        <DecisionTop>
                          <div>
                            <DecisionTitle>{alert.covenant?.covenantName || 'Covenant alert'}</DecisionTitle>
                            <DecisionMeta>{alert.message || alert.covenant?.borrowerName || 'Review covenant evidence'}</DecisionMeta>
                          </div>
                          <StatusPill $tone={statusTone(alert.alertType)}>{alert.alertType || 'ALERT'}</StatusPill>
                        </DecisionTop>
                      </DecisionItem>
                    ))}
                  </DecisionList>
                )}
              </PanelBody>
            </WorkspacePanel>
          </TwoColumn>
        </Stack>

        <Stack>
          <WorkspacePanel>
            <PanelHeader>
              <div>
                <SectionKicker>Credit Risk</SectionKicker>
                <PanelTitle>Watchlist & Workout</PanelTitle>
                <PanelSubtitle>Borrowers flagged by deterioration scans and active distressed workflows.</PanelSubtitle>
              </div>
              <IconBadge $tone={state.watchlist.length > 0 || totals.activeDistressed > 0 ? 'red' : 'emerald'}><ShieldAlert /></IconBadge>
            </PanelHeader>
            <PanelBody>
              {!ready('watchlist', 'distressed') && (state.watchlist.length === 0 && state.distressed.length === 0) ? <EmptyStateBox>{sectionMessage('watchlist', 'distressed')}</EmptyStateBox> : state.watchlist.length === 0 && state.distressed.length === 0 ? (
                <EmptyStateBox>No watchlist or workout records are open.</EmptyStateBox>
              ) : (
                <DecisionList>
                  {state.watchlist.slice(0, 5).map(entry => (
                    <DecisionItem key={`watch-${entry.loanId}`} $tone={statusTone(entry.severity)}>
                      <DecisionTop>
                        <div>
                          <DecisionTitle>{entry.borrowerName || entry.loanIdentifier || 'Watchlist borrower'}</DecisionTitle>
                          <DecisionMeta>
                            {entry.sector || 'Sector pending'} · {moneyCompact(entry.currentExposure)} · {entry.signals?.[0]?.detail || 'Deterioration signal'}
                          </DecisionMeta>
                        </div>
                        <StatusPill $tone={statusTone(entry.severity)}>{entry.riskScore ?? '--'}</StatusPill>
                      </DecisionTop>
                    </DecisionItem>
                  ))}
                  {state.distressed.slice(0, 4).map(situation => (
                    <DecisionItem key={`distressed-${situation.id}`} $tone={statusTone(situation.status)}>
                      <DecisionTop>
                        <div>
                          <DecisionTitle>{situation.companyName || 'Distressed situation'}</DecisionTitle>
                          <DecisionMeta>
                            {situation.situationType || 'Workout'} · {situation.industry || 'Industry pending'} · {moneyCompact(situation.totalExposure)}
                          </DecisionMeta>
                        </div>
                        <StatusPill $tone={statusTone(situation.status)}>{situation.status || 'OPEN'}</StatusPill>
                      </DecisionTop>
                    </DecisionItem>
                  ))}
                </DecisionList>
              )}
            </PanelBody>
          </WorkspacePanel>

          <WorkspacePanel>
            <PanelHeader>
              <div>
                <SectionKicker>Exposure</SectionKicker>
                <PanelTitle>Sponsor Concentration</PanelTitle>
                <PanelSubtitle>Funded exposure by mapped private-credit sponsor.</PanelSubtitle>
              </div>
              <IconBadge $tone="violet"><GitBranch /></IconBadge>
            </PanelHeader>
            <PanelBody>
              {!ready('leveragedLoans', 'privateCreditLoans') && (sponsorConcentration.length === 0) ? <EmptyStateBox>{sectionMessage('leveragedLoans', 'privateCreditLoans')}</EmptyStateBox> : sponsorConcentration.length === 0 ? (
                <EmptyStateBox>No sponsor exposure is mapped on current private credit records.</EmptyStateBox>
              ) : (
                <DecisionList>
                  {sponsorConcentration.map(row => (
                    <DecisionItem key={row.sponsor} $tone="violet">
                      <DecisionTop>
                        <div>
                          <DecisionTitle>{row.sponsor}</DecisionTitle>
                          <DecisionMeta>{((row.exposure / Math.max(totals.funded, 1)) * 100).toFixed(1)}% of funded loan exposure</DecisionMeta>
                        </div>
                        <StatusPill $tone="violet">{moneyCompact(row.exposure)}</StatusPill>
                      </DecisionTop>
                    </DecisionItem>
                  ))}
                </DecisionList>
              )}
            </PanelBody>
          </WorkspacePanel>

          <WorkspacePanel>
            <PanelHeader>
              <div>
                <SectionKicker>Controls</SectionKicker>
                <PanelTitle>Operating Coverage</PanelTitle>
                <PanelSubtitle>Live platform surfaces supporting the credit front-to-middle workflow.</PanelSubtitle>
              </div>
              <IconBadge $tone="cyan"><Gauge /></IconBadge>
            </PanelHeader>
            <PanelBody>
              <SourceGrid>
                <SourceCard>
                  <IconBadge $tone="blue"><Landmark /></IconBadge>
                  <SourceValue>{ready('leveragedLoans') ? numberCompact(state.loans.filter(l => l.source === 'LEVERAGED_LOAN').length) : '—'}</SourceValue>
                  <SourceLabel>Syndicated loans</SourceLabel>
                </SourceCard>
                <SourceCard>
                  <IconBadge $tone="violet"><Briefcase /></IconBadge>
                  <SourceValue>{ready('privateCreditLoans') ? numberCompact(state.loans.filter(l => l.source === 'PRIVATE_CREDIT').length) : '—'}</SourceValue>
                  <SourceLabel>Private credit loans</SourceLabel>
                </SourceCard>
                <SourceCard>
                  <IconBadge $tone="amber"><FileWarning /></IconBadge>
                  <SourceValue>{ready('covenantAlerts') ? numberCompact(state.covenantAlerts.length) : '—'}</SourceValue>
                  <SourceLabel>Covenant alerts</SourceLabel>
                </SourceCard>
                <SourceCard>
                  <IconBadge $tone="red"><AlertTriangle /></IconBadge>
                  <SourceValue>{ready('distressed') ? numberCompact(totals.activeDistressed) : '—'}</SourceValue>
                  <SourceLabel>Workout cases</SourceLabel>
                </SourceCard>
                <SourceCard>
                  <IconBadge $tone="emerald"><Activity /></IconBadge>
                  <SourceValue>{ready('trades') ? numberCompact(openTrades.length) : '—'}</SourceValue>
                  <SourceLabel>Open ticket items</SourceLabel>
                </SourceCard>
                <SourceCard>
                  <IconBadge $tone="cyan"><TrendingUp /></IconBadge>
                  <SourceValue>{ready('dashboard') ? moneyCompact(state.dashboard?.secondaryMarket?.totalActiveFaceValue) : '—'}</SourceValue>
                  <SourceLabel>Active secondary face</SourceLabel>
                </SourceCard>
              </SourceGrid>
            </PanelBody>
          </WorkspacePanel>
        </Stack>
      </LayoutGrid>
    </Shell>
  );
};

export default CreditOperatingSystemDashboard;
